import { useEffect, useRef } from 'react';
import { MOBILE_ISLAND_SEARCH_FOCUS_SCROLL_GRACE_MS } from './constants';
import { WORKBENCH_MOBILE_LIST_SCROLL_SELECTOR } from '../../utils/mobileScrollChrome';

const DASHBOARD_MOBILE_SCROLL_SELECTOR = '.dashboard-home-mobile-scroll';

const SCROLL_COLLAPSE_DELTA_PX = 8;
const SCROLL_COLLAPSE_COOLDOWN_MS = 420;

const findMobileListScrollEl = () =>
	document.querySelector(WORKBENCH_MOBILE_LIST_SCROLL_SELECTOR) ?? document.querySelector(DASHBOARD_MOBILE_SCROLL_SELECTOR);

/**
 * 搜索岛展开时：列表手指向下滑（scrollTop 减小）则收起双行搜索 UI。
 * 向上滑查看下方内容 / 滑到底部分页不触发收起。
 * 搜索已聚焦时短暂忽略滚动，避免键盘顶起列表误触发。
 */
export const useMobileIslandSearchCollapseOnScroll = (searchExpanded, onCollapse, searchFocused = false) => {
	const onCollapseRef = useRef(onCollapse);
	onCollapseRef.current = onCollapse;
	const focusGraceUntilRef = useRef(0);

	useEffect(() => {
		if (!searchExpanded || !searchFocused) {
			focusGraceUntilRef.current = 0;
			return undefined;
		}

		focusGraceUntilRef.current = Date.now() + MOBILE_ISLAND_SEARCH_FOCUS_SCROLL_GRACE_MS;
		const timer = window.setTimeout(() => {
			focusGraceUntilRef.current = 0;
		}, MOBILE_ISLAND_SEARCH_FOCUS_SCROLL_GRACE_MS);

		return () => window.clearTimeout(timer);
	}, [searchExpanded, searchFocused]);

	useEffect(() => {
		if (!searchExpanded) {
			focusGraceUntilRef.current = 0;
			return undefined;
		}

		let scrollEl = findMobileListScrollEl();
		let observer;
		let lastScrollY = scrollEl?.scrollTop ?? 0;
		let cooldownUntil = 0;

		const handleScroll = () => {
			if (!scrollEl || Date.now() < cooldownUntil) {
				return;
			}
			if (Date.now() < focusGraceUntilRef.current) {
				lastScrollY = scrollEl.scrollTop;
				return;
			}
			const scrollY = scrollEl.scrollTop;
			const delta = scrollY - lastScrollY;
			if (delta <= -SCROLL_COLLAPSE_DELTA_PX) {
				cooldownUntil = Date.now() + SCROLL_COLLAPSE_COOLDOWN_MS;
				onCollapseRef.current?.();
			}
			lastScrollY = scrollY;
		};

		const bind = (el) => {
			if (!el) {
				return undefined;
			}
			lastScrollY = el.scrollTop;
			el.addEventListener('scroll', handleScroll, { passive: true });
			return () => el.removeEventListener('scroll', handleScroll);
		};

		let unbind = bind(scrollEl);

		if (!scrollEl) {
			observer = new MutationObserver(() => {
				const next = findMobileListScrollEl();
				if (!next) {
					return;
				}
				unbind?.();
				scrollEl = next;
				unbind = bind(scrollEl);
				observer?.disconnect();
				observer = null;
			});
			observer.observe(document.body, { childList: true, subtree: true });
		}

		return () => {
			unbind?.();
			observer?.disconnect();
		};
	}, [searchExpanded]);
};
